import "./calendar.scss";
import * as React from "react";
import moment, { type Moment } from "moment";
import { debounce } from "throttle-debounce";
import withMediaQuery, { type InjectedMediaQueryProps } from "../../../developer/Utilities/MediaQuery";
import { isRecord } from "../../../types/internal/utility";
import { uppercaseFirst } from "../../Utilities/Generic/Strings";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import Button from "../Buttons/Button";
import CalendarModeSelection, { type CalendarModeType } from "./CalendarModeSelection";
import DatePicker from "./DatePicker/DatePicker";
import MonthPicker from "./MonthPicker/MonthPicker";
import TimePicker from "./TimePicker/TimePicker";

type DefaultValuePropType = Moment | string | undefined | null;

export interface CalendarChangeValue {
    start: Moment | null;
    end: Moment | null;
}

export function isCalendarChangeValue(value: unknown): value is CalendarChangeValue {
    if(!isRecord(value)) {
        return false;
    }

    return (
        "start" in value && (
            value.start === null || moment.isMoment(value.start)
        )
    ) && (
        "end" in value && (
            value.end === null || moment.isMoment(value.end)
        )
    );
}

export interface CalendarProps extends InjectedMediaQueryProps {
    name: string;
    onChange?: (value: CalendarChangeValue, name: string) => void;
    onClear?: (name: string) => void;
    onDone?: () => void;
    range?: boolean;
    time?: boolean;
    start?: Moment | null;
    end?: Moment | null;
    defaultValue: [DefaultValuePropType, DefaultValuePropType];
    selectTimeAfterDate?: boolean;
    preventReset?: boolean;
}

interface TypedValueType {
    date: string;
    time: string;
}

interface SelectingType {
    input: "date" | "time";
    value: "start" | "end";
}

interface State {
    mode: CalendarModeType;
    start: Moment | null;
    end: Moment | null;
    viewing: Moment;
    hover: Moment | null;
    typedStart: TypedValueType;
    typedEnd: TypedValueType;
    typedOld: {
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
    };
    selecting: SelectingType | null;
}

interface InputRefs {
    startDate: React.RefObject<HTMLInputElement | null>;
    startTime: React.RefObject<HTMLInputElement | null>;
    endDate: React.RefObject<HTMLInputElement | null>;
    endTime: React.RefObject<HTMLInputElement | null>;
}

type RefKeys = keyof InputRefs;

function parseDefault(value: DefaultValuePropType): Moment | null {
    if(typeof value === "undefined" || value == null) {
        return null;
    }

    if(typeof value === "string") {
        // Parse string
        const parsed = moment(value);

        if(parsed.isValid()) {
            return parsed;
        }
    } else if(moment.isMoment(value)) {
        // Check to see if moment parsed to the function is valid
        if(value.isValid()) {
            return value;
        }
    }

    // Set value as current time
    return moment();
}

export class Calendar extends React.Component<CalendarProps, State> {
    public static defaultProps = {
        defaultValue: [ undefined, undefined ],
    } as {
        defaultValue: [DefaultValuePropType, DefaultValuePropType];
    };

    public inputRefs: InputRefs = {
        startDate: React.createRef(),
        startTime: React.createRef(),
        endDate  : React.createRef(),
        endTime  : React.createRef(),
    };

    constructor(props: Readonly<CalendarProps>) {
        super(props);

        const start = parseDefault(props.defaultValue?.[0]);

        const typedStart = {
            date: "",
            time: "",
        };

        if(moment.isMoment(start)) {
            typedStart.date = start.format("YYYY-MM-DD");
            typedStart.time = start.format("HH:mm:ss");
        }

        let viewing;

        if(moment.isMoment(start)) {
            viewing = start.clone().startOf("month");
        } else {
            viewing = moment().startOf("month");
        }

        let end = null;

        if(props.range) {
            end = parseDefault(props.defaultValue?.[1]);
        }

        const typedEnd = {
            date: "",
            time: "",
        };

        if(end !== null && moment.isMoment(end)) {
            typedEnd.date = end.format("YYYY-MM-DD");
            typedEnd.time = end.format("HH:mm:ss");
        }

        this.state = {
            mode     : "date",
            selecting: {
                input: "date",
                value: "start",
            },
            typedOld: {
                startDate: "",
                startTime: "",
                endDate  : "",
                endTime  : "",
            },
            hover: null,
            typedStart,
            typedEnd,
            viewing,
            start,
            end,
        };

        this.updateValue = debounce(200, this.updateValue.bind(this));
    }

    public componentDidUpdate(prevProps: Readonly<CalendarProps>, prevState: Readonly<State>): void {
        if(prevState.start !== this.state.start || prevState.end !== this.state.end) {
            if(typeof this.props.onChange === "function") {
                let { start, end } = this.state;

                // Check to see if the values should be swapped
                if(moment.isMoment(start) && moment.isMoment(end)) {
                    if(start.isAfter(end)) {
                        // Swap the start and end dates but keep the times
                        const temp = end
                            .clone()
                            .hours(start.hours())
                            .minutes(start.minutes())
                            .seconds(start.seconds());

                        end = start
                            .clone()
                            .hours(end.hours())
                            .minutes(end.minutes())
                            .seconds(end.seconds());
                        start = temp;
                    }
                }

                this.updateValue({
                    start,
                    end,
                });
            }
        }
    }

    public updateValue = (value: CalendarChangeValue): void => {
        if(typeof this.props.onChange === "function") {
            this.props.onChange(value, this.props.name);
        }
    };

    public handleHoverChange = (hover: Moment | null): void => {
        this.setState({
            hover,
        });
    };

    public handleViewChange = (viewing: Moment): void => {
        this.setState({
            viewing,
        });
    };

    public handleMonthChange = (viewing: Moment): void => {
        this.setState({
            mode: "date",
            viewing,
        });
    };

    public handleDateChange = (value: Moment): void => {
        let name: "start" | "end" = "start";

        if(this.state.selecting && this.state.selecting.input === "date") {
            name = this.state.selecting.value;
        }

        const typedName = `typed${uppercaseFirst(name)}` as "typedStart" | "typedEnd";

        this.setState(prevState => {
            let newValue = prevState[name];

            if(moment.isMoment(newValue)) {
                newValue = newValue.clone()
                    .year(value.year())
                    .month(value.month())
                    .date(value.date());
            } else if(name === "end") {
                newValue = value.clone()
                    .endOf("day");
            } else {
                newValue = value.clone();
            }

            return {
                [typedName]: {
                    ...prevState[typedName],
                    date: value.format("YYYY-MM-DD"),
                },
                [name]   : newValue,
                selecting: {
                    input: "date",
                    value: name,
                },
            } as Partial<State> as State; // I couldn't find a better way to do this
        }, this.moveToNext);
    };

    public handleTimeChange = (name: "start" | "end", value: Moment): void => {
        const typedName = `typed${uppercaseFirst(name)}` as "typedStart" | "typedEnd";

        this.setState(prevState => {
            // Update the old value to keep the date values if necessary
            let newValue = prevState[name];

            if(moment.isMoment(newValue)) {
                newValue = newValue.clone()
                    .hours(value.hours())
                    .minutes(value.minutes())
                    .seconds(value.seconds());
            } else {
                newValue = value;
            }

            return {
                [typedName]: {
                    ...prevState[typedName],
                    time: value.format("HH:mm:ss"),
                },
                [name]: newValue,
            } as Partial<State> as State; // I couldn't find a better way to do this
        });
    };

    public handleModeChange = (mode: CalendarModeType): void => {
        this.setState({
            mode,
        });
    };

    public handleReset = (): void => {
        if(typeof this.props.onClear === "function") {
            this.props.onClear(this.props.name);
        }

        this.setState({
            mode      : "date",
            typedStart: { date: "", time: "" },
            typedEnd  : { date: "", time: "" },
            viewing   : moment().startOf("month"),
            start     : null,
            end       : null,
            selecting : {
                input: "date",
                value: "start",
            },
        });
    };

    public handleDone = (): void => {
        if(typeof this.props.onDone === "function") {
            this.props.onDone();
        }
    };

    public handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, start: boolean): void => {
        const typedName = start ? "typedStart" : "typedEnd";

        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        // Check to see if all 4 characters of the year have been typed
        const [ year ] = value.split("-");
        const fullYear = parseInt(year, 10).toString(10).length >= 4;

        const parsed = moment(value, (name === "date" ? "YYYY-MM-DD" : "HH:mm:ss"));

        this.setState(prevState => {
            const newState: Partial<State> = {
                [typedName]: {
                    ...prevState[typedName],
                    [name]: value,
                },
            };

            if(parsed.isValid()) {
                const parsedName = (start ? "start" : "end") as "start" | "end";

                let parsedValue = prevState[parsedName];

                if(moment.isMoment(parsedValue)) {
                    if(name === "date") {
                        parsedValue = parsedValue.clone()
                            .year(parsed.year())
                            .month(parsed.month())
                            .date(parsed.date());
                    } else {
                        parsedValue = parsedValue.clone()
                            .hours(parsed.hours())
                            .minutes(parsed.minutes())
                            .seconds(parsed.seconds());
                    }
                } else {
                    parsedValue = parsed;
                }

                newState[parsedName] = parsedValue;
            }

            return newState as State;
        }, () => {
            // Check to see if the values haven't been filed out already
            const { typedStart, typedEnd } = this.state;

            if(
                !(
                    !!typedStart.date &&
                    !!typedEnd.date && (
                        !this.props.time || (
                            !!typedStart.time &&
                            !!typedEnd.time
                        )
                    )
                ) &&
                fullYear
            ) {
                this.moveToNext(); // Move to next input
            }
        });
    };

    public handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        return this.handleInputChange(e, true);
    };

    public handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        return this.handleInputChange(e, false);
    };

    public handleSelectingChange = (input: "time" | "date", value: "start" | "end"): void => {
        this.setState({
            mode     : input,
            selecting: {
                input,
                value,
            },
        });
    };

    public handleStartFocus = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.handleSelectingChange(e.currentTarget.name as "time" | "date", "start");
    };

    public handleEndFocus = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.handleSelectingChange(e.currentTarget.name as "time" | "date", "end");
    };

    public moveToNext = (): void => {
        const selecting = this.state.selecting;

        if(!selecting) {
            return;
        }

        let newSelecting: SelectingType | null = {
            ...selecting,
        };

        if(selecting.value === "start") {
            if(selecting.input === "date") {
                if(this.props.range) {
                    newSelecting.value = "end";
                } else if(this.props.selectTimeAfterDate && this.props.time) {
                    newSelecting.input = "time";
                } else {
                    newSelecting = null;
                }
            } else if(!this.props.range) {
                newSelecting = null;
            }
        } else if(selecting.value === "end" && this.props.range) {
            if(selecting.input === "date") {
                if(this.props.selectTimeAfterDate && this.props.time) {
                    newSelecting = {
                        input: "time",
                        value: "start",
                    };
                } else {
                    newSelecting = null;
                }
            }
        }

        this.setState({
            selecting: newSelecting,
        }, () => {
            if(newSelecting) {
                const ref = this.inputRefs[`${newSelecting.value}${uppercaseFirst(newSelecting.input)}` as RefKeys];

                if(ref && ref.current) {
                    ref.current.focus();
                }
            }
        });
    };

    public onInputFocus = (
        name: "start" | "end",
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => void,
        time = false
    ): (e: React.FocusEvent<HTMLInputElement>) => void => {
        return (e: React.FocusEvent<HTMLInputElement>): void => {
            const typedKey = `typed${uppercaseFirst(name)}` as "typedStart" | "typedEnd";
            const key = (time ? "time" : "date");

            const oldKey = `${name}${(time ? "Time" : "Date")}` as RefKeys;

            this.setState(prevState => {
                const value = prevState[typedKey][key];

                return {
                    [typedKey]: {
                        ...prevState[typedKey],
                        [key]: "",
                    },
                    typedOld: {
                        ...prevState.typedOld,
                        [oldKey]: value,
                    },
                } as Partial<State> as State;
            });

            onFocus(e);
        };
    };

    public onInputBlur = (
        name: "start" | "end",
        time = false
    ): () => void => {
        return (): void => {
            const typedKey = `typed${uppercaseFirst(name)}` as "typedStart" | "typedEnd";
            const key = (time ? "time" : "date");

            const oldKey = `${name}${(time ? "Time" : "Date")}` as RefKeys;

            this.setState(prevState => {
                const value = prevState[typedKey][key];

                if(value) {
                    return {};
                }

                return {
                    [typedKey]: {
                        ...prevState[typedKey],
                        [key]: prevState.typedOld[oldKey],
                    },
                    typedOld: {
                        ...prevState.typedOld,
                        [oldKey]: "",
                    },
                } as Partial<State> as State; // I couldn't find a better way to do this
            });
        };
    };

    public renderHeaderInput = (
        name: "start" | "end",
        value: TypedValueType,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => void,
        autoFocus = false
    ): React.ReactNode => {
        let selected = "";

        if(this.state.selecting && this.state.selecting.value === name) {
            selected = this.state.selecting.input;
        }

        const dateKey = `${name}Date` as RefKeys;
        const timeKey = `${name}Time` as RefKeys;

        return (
            <div className={"calendar-header-input" + (this.props.time ? " time" : "")}>
                <input
                    ref={this.inputRefs[dateKey]}
                    type="date"
                    name="date"
                    className={(selected === "date" ? "selecting" : "")}
                    value={value.date}
                    onChange={onChange}
                    onFocus={this.onInputFocus(name, onFocus)}
                    onBlur={this.onInputBlur(name)}
                    autoFocus={autoFocus}
                />
                {this.props.time && (
                    <input
                        ref={this.inputRefs[timeKey]}
                        type="time"
                        step="1"
                        name="time"
                        className={(selected === "time" ? "selecting" : "")}
                        value={value.time}
                        onChange={onChange}
                        onFocus={this.onInputFocus(name, onFocus, true)}
                        onBlur={this.onInputBlur(name, true)}
                    />
                )}
            </div>
        );
    };

    public renderBody = (): React.ReactNode => {
        switch(this.state.mode) {
            case "month":
                return (
                    <MonthPicker
                        mobile={this.props.matchesMedia}
                        range={this.props.range}
                        start={this.state.start}
                        end={this.state.end}
                        viewing={this.state.viewing}
                        onClick={this.handleMonthChange}
                        onView={this.handleViewChange}
                    />
                );
            case "date":
                return (
                    <DatePicker
                        mobile={this.props.matchesMedia}
                        range={this.props.range}
                        start={this.state.typedStart.date ? this.state.start : null}
                        end={this.state.typedEnd.date ? this.state.end : null}
                        hover={this.state.hover}
                        viewing={this.state.viewing}
                        onClick={this.handleDateChange}
                        onView={this.handleViewChange}
                        onModeChange={this.handleModeChange}
                        onHoverChange={this.handleHoverChange}
                    />
                );
            case "time":
                return (
                    <TimePicker
                        mobile={this.props.matchesMedia}
                        range={!!this.props.range}
                        start={this.state.start}
                        end={this.state.end}
                        onChange={this.handleTimeChange}
                    />
                );
        }
    };

    public render(): React.ReactNode {
        return (
            <div className="calendar">
                <div className="calendar-header">
                    {this.renderHeaderInput(
                        "start",
                        this.state.typedStart,
                        this.handleStartInputChange,
                        this.handleStartFocus,
                        true
                    )}
                    {this.props.range && (
                        <React.Fragment>
                            <div className="calendar-header-input-separator">
                                <FontAwesomeLight icon="minus" />
                            </div>
                            {this.renderHeaderInput(
                                "end",
                                this.state.typedEnd,
                                this.handleEndInputChange,
                                this.handleEndFocus
                            )}
                        </React.Fragment>
                    )}
                </div>
                <div className="calendar-body">
                    {this.renderBody()}
                </div>
                <div className="calendar-footer">
                    <div className="calendar-mode-selector">
                        <CalendarModeSelection
                            mode="date"
                            icon="calendar-alt"
                            selected={this.state.mode}
                            onSelect={this.handleModeChange}
                        />
                        {this.props.time && (
                            <CalendarModeSelection
                                mode="time"
                                icon="clock"
                                selected={this.state.mode}
                                onSelect={this.handleModeChange}
                            />
                        )}
                    </div>
                    <div className="calendar-footer-buttons">
                        {!this.props.preventReset && (
                            <Button
                                type="button"
                                classes={[ "calendar-reset" ]}
                                onClick={this.handleReset}
                            >
                                Reset
                            </Button>
                        )}
                        <Button
                            type="button"
                            classes={[ "calendar-done" ]}
                            onClick={this.handleDone}
                        >
                            Done
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export default withMediaQuery(Calendar, "(max-width: 700px)");
